import type { Middleware } from '@reduxjs/toolkit';
import { logger } from 'common/services';

import { openNatterFeature } from '../OpenNatterFeature';
import { connectSocket, socketConnected, socketDisconnected, subscribeToOpenNatter } from '../state/openNatter.actions';
import type { OpenNatterMessage } from '../types/messages';
import { SocketEvents } from './OpenNatterSocket';
import { dispatchOpenNatterMessage } from './openNatterMessageDispatcher';
import { sequenceChecker } from './sequenceChecker';

/**
 * Open Natter socket middleware
 * Connects the WebSocket to Redux, dispatching messages as actions
 * Socket listeners are set up when connectSocket or subscribeToOpenNatter is dispatched
 */
export const openNatterSocketMiddleware: Middleware = (store) => {
  let socketListenersSetUp = false;

  const setupSocketListeners = () => {
    if (socketListenersSetUp) return;

    const socket = openNatterFeature.getSocket();

    // Listen for WebSocket connection events
    socket.on(SocketEvents.connected, () => {
      logger.info('[OpenNatterSocketMiddleware] Socket connected');
      store.dispatch(socketConnected());
    });

    socket.on(SocketEvents.reconnected, () => {
      logger.info('[OpenNatterSocketMiddleware] Socket reconnected');
      store.dispatch(socketConnected());
    });

    socket.on(SocketEvents.disconnected, () => {
      logger.info('[OpenNatterSocketMiddleware] Socket disconnected');
      store.dispatch(socketDisconnected());
    });

    // Listen for WebSocket messages and dispatch them to Redux
    socket.on('message', ({ destination, payload }: { destination: string; payload: OpenNatterMessage }) => {
      logger.debug('[OpenNatterSocketMiddleware] Received message', {
        destination,
        messageType: payload.type,
      });

      try {
        // Process message through sequence checker
        // This ensures messages are dispatched in order
        const messagesToDispatch = sequenceChecker.processMessage(payload);

        // Dispatch all messages (in order)
        for (const message of messagesToDispatch) {
          dispatchOpenNatterMessage(store.dispatch, message);
        }
      } catch (error) {
        logger.error('[OpenNatterSocketMiddleware] Error processing message', {
          error,
          destination,
          messageType: payload.type,
        });
      }
    });

    // Listen for WebSocket errors
    socket.on('error', (error: Error) => {
      logger.error('[OpenNatterSocketMiddleware] WebSocket error', { error });
    });

    socketListenersSetUp = true;
  };

  return (next) => (action) => {
    // Set up socket listeners when connecting or subscribing to Open Natter
    if (connectSocket.match(action) || subscribeToOpenNatter.match(action)) {
      setupSocketListeners();
    }
    return next(action);
  };
};
